import React, { useContext } from 'react';
import { iterationKey, repeatIfDaily } from '../components/calendar/utils';
import { CalendarPsaContext } from './calendarPsaContext';
import {
  EmneDto,
  PraksiskalenderAktivitetDto,
  PraksiskalenderInstitusjonerDto,
} from '../api/generated/praksissted-generated-api';
import CalendarCell from './calendarCell';
import { formatIsoTzLess, getWeekDay, WeekYear } from '../date.utils';
import { listIsDefinedAndHasElements } from '../util/listutils';

interface RowProps {
  shouldRenderPracticeTypeHeader: boolean;
  shouldRenderInstitustionHeader: boolean;
  institusjon: PraksiskalenderInstitusjonerDto;
  klassenavn: string;
  emne: EmneDto;
  praksistypenavn: string;
  aktiviteter: PraksiskalenderAktivitetDto[];
}

const CalendarRow: React.FC<React.PropsWithChildren<RowProps>> = ({
  shouldRenderPracticeTypeHeader,
  shouldRenderInstitustionHeader,
  institusjon,
  klassenavn,
  emne,
  praksistypenavn,
  aktiviteter,
}) => {
  const { weekNumbers, earliestVisibleIsoDate, isWeekly, handleWeekHover } =
    useContext(CalendarPsaContext);
  const aktivitetPrDag: {
    [startDato: string]: PraksiskalenderAktivitetDto;
  } = {};
  (aktiviteter || []).forEach(
    (aktivitet) => (aktivitetPrDag[aktivitet.startDato] = aktivitet),
  );

  const { kode: emnekode, navn: emnenavn } = emne;

  const getExtendedPracticePeriod = function (
    week: WeekYear,
    day: number,
  ): PraksiskalenderAktivitetDto | null {
    if (!listIsDefinedAndHasElements(aktiviteter)) {
      return null;
    }
    const periodForWeekday = function (weekDay: number) {
      const thisDayIso = formatIsoTzLess(getWeekDay(week, weekDay));
      const isFirstColumn = week === weekNumbers[0] && weekDay === 0;
      return (
        aktivitetPrDag[thisDayIso] ||
        (isFirstColumn &&
          aktiviteter.find(
            (it) =>
              it.startDato < earliestVisibleIsoDate &&
              it.sluttDato >= thisDayIso,
          ))
      );
    };
    const outputForPeriod = function (
      period: PraksiskalenderAktivitetDto | undefined,
    ) {
      return period
        ? {
            ...period,
            emneName: emnenavn,
            practiceTypeName: praksistypenavn,
          }
        : null;
    };

    if (!isWeekly) {
      return outputForPeriod(periodForWeekday(day - 1));
    } else {
      const period = [0, 1, 2, 3, 4, 5, 6]
        .map(periodForWeekday)
        .find((it) => it);
      return outputForPeriod(period);
    }
  };

  const dayToCellMappingFunc = function (dayOfWeek: number, week: WeekYear) {
    const period = (day: number) => getExtendedPracticePeriod(week, day);
    return (
      <CalendarCell
        key={iterationKey(week, dayOfWeek)}
        weekYear={week}
        mouseIn={handleWeekHover}
        mouseOut={handleWeekHover}
        practicePeriod={period(dayOfWeek)}
      />
    );
  };

  return (
    <>
      {shouldRenderPracticeTypeHeader && (
        <tr className="practice-type-header">
          <td>{praksistypenavn}</td>
          {weekNumbers.map((it: WeekYear) => {
            return repeatIfDaily(isWeekly, (iDay) => (
              <td key={iterationKey(it, iDay)}>&nbsp;</td>
            ));
          })}
        </tr>
      )}
      {shouldRenderInstitustionHeader && (
        <tr className="institution-header">
          <td>{institusjon.institusjonsnavn}</td>
          {weekNumbers.map((it: WeekYear) => {
            return repeatIfDaily(isWeekly, (iDay) => (
              <td key={iterationKey(it, iDay)}>&nbsp;</td>
            ));
          })}
        </tr>
      )}
      <tr className="emne with-groups">
        <td>
          <div className="caption">
            <span className="code">{emnekode}</span>
            <span title={klassenavn} className="group-letter">
              {` - ${klassenavn}`}
            </span>
            <br />
            {emnenavn}
          </div>
        </td>
        {weekNumbers.map((week: WeekYear) =>
          repeatIfDaily(isWeekly, (iDay) => dayToCellMappingFunc(iDay, week)),
        )}
      </tr>
    </>
  );
};

export default CalendarRow;
